import { useMemo, useState } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useSites } from '../../../context/SiteContext';
import { useReportVisits } from '../../../hooks/useReportVisits';
import { buildHourly } from '../../../lib/analytics';
import { formatNumber, startOfDay } from '../../../lib/format';
import { toInputDate } from '../../../lib/range';
import EmptyState from '../../../components/dashboard/EmptyState';
import PageHeader from '../../../components/dashboard/PageHeader';
import StatTable from '../../../components/dashboard/StatTable';
import ExportButton from '../../../components/dashboard/ExportButton';

export default function HourlyPage() {
  const { currentSite } = useSites();
  const { humans } = useReportVisits(currentSite);
  const [day, setDay] = useState(toInputDate(startOfDay()));

  const picked = useMemo(() => {
    const [y, m, d] = day.split('-').map(Number);
    return buildHourly(humans, startOfDay(new Date(y, (m || 1) - 1, d || 1)));
  }, [humans, day]);

  const compare = useMemo(() => {
    const [y, m, d] = day.split('-').map(Number);
    const current = startOfDay(new Date(y, (m || 1) - 1, d || 1));
    const prev = new Date(current);
    prev.setDate(current.getDate() - 1);
    const yesterday = buildHourly(humans, prev);
    return picked.map((h, i) => ({
      ...h,
      prevPv: yesterday[i]?.pv || 0,
    }));
  }, [humans, day, picked]);

  if (!currentSite) {
    return <EmptyState title="先添加一个站点" desc="添加站点后可查看任意一天的分时统计。" />;
  }

  const columns = ['时段', '浏览量', '访客', 'IP', '较前一日'];
  const rows = compare.map((h) => [
    h.label,
    formatNumber(h.pv),
    formatNumber(h.uv),
    formatNumber(h.ip),
    formatNumber(h.prevPv),
  ]);

  return (
    <div>
      <PageHeader
        title="分时统计"
        desc="按小时查看 PV / UV / IP，并对比前一日同时段。"
        range={undefined}
        extra={
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-md border border-background-200"
            />
            <ExportButton filename={`${currentSite.domain}-hourly.csv`} columns={columns} rows={rows} />
          </div>
        }
      />
      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="text-sm font-semibold text-foreground-900">{day} 分时趋势</h2>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={compare}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={2} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} width={32} axisLine={false} tickLine={false} />
              <Tooltip />
              <Area type="monotone" dataKey="pv" stroke="#E25A3C" fill="#E25A3C33" name="浏览量" />
              <Area type="monotone" dataKey="uv" stroke="#2F9D6A" fill="#2F9D6A22" name="访客" />
              <Area type="monotone" dataKey="prevPv" stroke="#9A8B7A" fill="#9A8B7A22" name="前一日浏览量" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <StatTable columns={columns} rows={rows} pageSize={24} />
      </div>
    </div>
  );
}
